import { Stack, Typography } from "@mui/material";
import {
  createContext,
  useCallback,
  useMemo,
  useState,
  ReactNode,
  useContext,
} from "react";
import { InputType } from "src/api/types";
import DrawerForm from "src/shared/DrawerForm";
import Input from "src/components/FormInput";
import { useManageDrawer } from "src/hooks/useManageDrawer";
import { useSentryLogger } from "src/hooks/useSentryLogger";

export type DrawerContextType<T extends object> = {
  item: T | null;
  handleOpenUpdateItem: (item: T | null) => void;
};

export function createDrawerProvider<T extends object>(label: string) {
  const Context = createContext<DrawerContextType<T>>({
    item: null,
    handleOpenUpdateItem: () => {},
  });

  function useDrawerContext() {
    const ctx = useContext(Context);
    if (!ctx) {
      throw new Error("useDrawerContext must be used inside its Provider");
    }
    return ctx;
  }

  function DrawerProvider({
    children,
    getInputs,
    loading,
    onSubmit,
  }: {
    children: ReactNode;
    getInputs: (
      value: T,
      setValue: (key: keyof T, value: T[keyof T]) => void,
    ) => InputType<T>[];
    loading: boolean;
    onSubmit: (item: T) => void;
  }) {
    const logError = useSentryLogger();
    const [openUpdate, updateActions] = useManageDrawer();
    const [item, setItem] = useState<T | null>(null);
    const [error, setError] = useState<string | null>(null);

    const setFields = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
      setItem((prev) => {
        if (prev == null) return prev;
        return { ...prev, [key]: value };
      });
      setError(null);
    }, []);

    const handleOpenUpdateItem = useCallback(
      (item: T | null) => {
        setItem(item);
        updateActions.handleOpen();
      },
      [updateActions],
    );

    const value = useMemo(
      () => ({
        item,
        handleOpenUpdateItem,
      }),
      [item, handleOpenUpdateItem],
    );

    const onClose = useCallback(() => {
      setItem(null);
      updateActions.handleClose();
    }, [updateActions]);

    const handleSubmit = useCallback(
      (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        try {
          if (item != null) {
            onSubmit(item);
          }

          onClose();
        } catch (err) {
          logError("Error submitting form", err);
          setError(
            "An error occurred while submitting the form. Please try again.",
          );
        }
      },
      [item, logError, onClose, onSubmit],
    );

    const inputs = useMemo(() => {
      if (item == null) return [];
      return getInputs(item, setFields);
    }, [getInputs, item, setFields]);

    return (
      <Context.Provider value={value}>
        {children}

        {item && (
          <DrawerForm
            label={label}
            onClose={onClose}
            open={openUpdate}
            handleSubmit={handleSubmit}
            loading={loading}
          >
            <Stack gap={2}>
              {inputs.map((i) => (
                <Input key={i.label} current={i} />
              ))}
            </Stack>
            {error != null ? (
              <Typography color="error">{error}</Typography>
            ) : null}
          </DrawerForm>
        )}
      </Context.Provider>
    );
  }

  return { DrawerProvider, useDrawerContext };
}
